import React, { useMemo, useState } from 'react';
import classnames from 'classnames';
import dayjs from 'dayjs';
import IconFont from '@/components/icon-font';
import { AddOnProduct } from '@/types/product';
import { GIFT_LEVEL_MEIDOU_MAP, GIFT_LEVEL_OPTIONS, TabType } from '@/constants/subscription';
import ServiceNoticeModal from '@/components/service-notice-modal';
import { getUrlParam } from '@/utils';
import {
  PurchaseScene,
  SubscribeType,
  isFromPcHome,
  isTeamSubscription,
  purchaseScene,
  subscribeType,
} from '@/constants';
import { useAtom } from 'jotai';
import {
  activeIndexAtom,
  curProductAtom,
  curTabAtom,
  giftLevelAtom,
  productsAtom,
  teamInfoAtom,
  vipInfoAtom,
} from '@/store';
import { Select } from 'antd';

import styles from './index.module.css';
import { useLocaleStore } from '@/i18n/localeStore';
import { syncSubEntranceBizCode } from '@/utils/subscriptionEntrance';
import { getLanguage } from '@/i18n/constants';
import { isProPeriodTabExperiment } from '@/utils/abtest';

interface IProps {
  onRefresh?: () => void;
}

const ProductTip: React.FC<IProps> = (props: IProps) => {
  const { $t } = useLocaleStore();
  const { onRefresh } = props;
  const [visible, setVisible] = useState<boolean>(false);
  const [type, setType] = useState<string>('');

  const [curTab, setCurTab] = useAtom(curTabAtom);
  const [curProduct] = useAtom(curProductAtom);
  const [products, setProducts] = useAtom(productsAtom);
  const [activeIndex] = useAtom(activeIndexAtom);
  const [teamInfo] = useAtom(teamInfoAtom);
  const [vipInfo] = useAtom(vipInfoAtom);
  const [giftLevel, setGiftLevel] = useAtom(giftLevelAtom);

  const currentMeidouPerMonth = GIFT_LEVEL_MEIDOU_MAP[Number(giftLevel)] || 0;
  const isDoubleMeidouActivityProduct = [
    '7464212650826946000', // 高级会员一阶-连续包年-双倍美豆-活动群组
    '7464216881046777388', // 高级会员二阶-连续包年-双倍美豆-活动群组
    '7464228357287474732', // 高级会员三阶-连续包年-双倍美豆-活动群组
    '7464229324208764167', // 高级会员四阶-连续包年-双倍美豆-活动群组
  ].includes(curProduct?.productId || '');

  const isPcBatch = getUrlParam('type') === 'svip';

  const addOnProduct = useMemo<AddOnProduct | undefined>(() => {
    return curProduct?.addOn?.products?.[0];
  }, [curProduct]);

  const handleToggleAddOnSelected = () => {
    if (!addOnProduct || activeIndex < 0) return;

    setProducts(
      products.map((product, index) => {
        if (index !== activeIndex) return product;

        const newAddOnProducts = product.addOn?.products?.map((item) => {
          if (item.productId === addOnProduct.productId) {
            return { ...item, selected: !item.selected };
          }
          return item;
        });

        return {
          ...product,
          addOn: {
            ...product.addOn,
            products: newAddOnProducts,
          },
        };
      })
    );
  };

  if (!curProduct) {
    return (
      <div className={styles.module}>
        <div className={styles.skeleton}>&nbsp;</div>
      </div>
    );
  }

  const renderQuestionIcon = (productType: string) => {
    return (
      <div
        className={styles.tooltip}
        onClick={() => {
          setType(productType);
          setVisible(true);
        }}
      >
        <IconFont name="questionmarkCircle" fontSize={20} />
      </div>
    );
  };

  const handleRenderTips = () => {
    if (isPcBatch) {
      // 批处理
      return (
        <div
          className={styles.desc}
          dangerouslySetInnerHTML={{
            __html: $t((v, { r }) =>
              r(v.Desc_BatchCutoutConsumption, [
                `<span style="color:#fff;font-weight:bolder">&nbsp;10&nbsp;</span>`,
                `<span style="color:#fff;font-weight:bolder">&nbsp;1&nbsp;</span>`,
              ])
            ),
          }}
        ></div>
      );
    }

    // 团队版，购买美豆文案
    if (curTab === TabType.MEIDOU && isTeamSubscription) {
      return (
        <>
          <div className={styles.descInfo}>
            <IconFont name="infoCircleBold" fontSize={18} color="rgba(255, 255, 255, 0.50)" />
            {$t((v) => v.Desc_OneYearValidTeam)}
          </div>
          {!vipInfo?.is_vip ? (
            <div
              className={styles.desc}
              style={{
                fontSize: 14,
              }}
            >
              <span
                style={{
                  color: '#fff',
                }}
              >
                {$t((v) => v.Text_SharedMeidouPrompt)}
              </span>
              &nbsp;
              <span
                style={{
                  color: 'var(--text-link)',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  // 默认5个席位
                  const url = new URL(window.location.href);
                  const params = url.searchParams;
                  params.set('seats', '5');
                  window.history.replaceState({}, '', url);

                  // 切购买会员
                  setCurTab(TabType.SUB);
                }}
              >
                {$t((v) => v.Button_ActivateTeamMember)}
              </span>
            </div>
          ) : null}
        </>
      );
    }

    // 团队版，加购席位场景
    if (curTab === TabType.SUB && isTeamSubscription && purchaseScene === PurchaseScene.EXTRA) {
      return (
        <div className={styles.desc}>
          <span>
            {$t((v, { r }) =>
              r(v.Text_SeatPurchaseEffect, [
                dayjs((teamInfo?.active_order?.vip_end_time || 0) * 1000).format('YYYY-MM-DD'),
              ])
            )}
          </span>
        </div>
      );
    }

    // 团队版，购买会员场景
    if (curTab === TabType.SUB && isTeamSubscription) {
      // 低价试用是商品买赠，非试用商品是会员买赠
      const giftExplain = curProduct.isProbation
        ? curProduct.checkBox?.giftExplain
        : curProduct.checkBox?.vipGiftExplain;

      return giftExplain ? (
        <div className={styles.desc}>
          <span>{giftExplain}</span>
        </div>
      ) : null;
    }

    // 是否展示买赠文案模块
    // PC 站点的高级会员不走「每月赠送 + 美豆档位选择」那套，与标准会员一致，按商品买赠文案展示
    const isPcHomePro = isFromPcHome && curTab === TabType.PRO_SUB;
    const showGiftText =
      (curTab === TabType.PRO_SUB && !isPcHomePro && !isProPeriodTabExperiment()) ||
      (!isTeamSubscription &&
        (curTab === TabType.SUB || isPcHomePro) &&
        (curProduct.checkBox?.vipGiftExplain || curProduct.checkBox?.giftExplain));

    // 有选中加购时优先展示加购商品续期文案
    const renewExplain =
      (addOnProduct?.selected && addOnProduct.checkBox?.explain) || curProduct.checkBox?.explain;

    return (
      <>
        {showGiftText ? (
          <>
            <div className={styles.giftLevelRow}>
              {getLanguage() === 'zh-Hans' ? (
                <span className={styles.giftLevelIcon}>{$t((v) => v.Label_Gift)}</span>
              ) : null}

              {curTab === TabType.PRO_SUB && !isPcHomePro ? (
                <>
                  <span className={styles.giftLevelLabel}>
                    {isDoubleMeidouActivityProduct
                      ? $t((v, { r }) =>
                          r(v.Text_FirstMonthMeidouGift, [
                            currentMeidouPerMonth * 2,
                            currentMeidouPerMonth,
                          ])
                        )
                      : $t((v) => v.Text_MonthlyMeidouGift)}
                  </span>
                  <Select
                    value={giftLevel}
                    size="small"
                    options={GIFT_LEVEL_OPTIONS}
                    className={styles.giftLevelSelect}
                    onChange={(value) => {
                      setGiftLevel(value);
                      syncSubEntranceBizCode();
                      onRefresh?.();
                    }}
                    dropdownClassName="giftLevelDropdown"
                  ></Select>
                </>
              ) : (
                <div className={styles.giftLevelLabel}>
                  {/* 个人版，会员买赠文案 + 商品买赠文案 */}
                  {`${curProduct.checkBox?.vipGiftExplain || ''}${
                    curProduct.checkBox?.giftExplain || ''
                  }`}
                </div>
              )}
            </div>
          </>
        ) : null}

        {/* 单购场景，且购买张数 */}
        {subscribeType === SubscribeType.CONSUMABLE && curTab === TabType.CUTOUT ? (
          <>
            {isTeamSubscription ? (
              <div className={classnames(styles.descInfo)}>
                <IconFont name="infoCircleBold" fontSize={18} color="rgba(255, 255, 255, 0.50)" />
                {$t((v) => v.Desc_TeamOnlyCutout)}
              </div>
            ) : null}
            <div className={styles.desc}>
              <span>{$t((v) => v.Text_DownloadDeductRule)}</span>
            </div>
            <div className={styles.desc}>
              <span>{$t((v) => v.Text_CutoutAdjustRule)}</span>
            </div>
          </>
        ) : null}

        {/* 抠图订阅场景下且购买会员，需要展示的固定文案 */}
        {subscribeType === SubscribeType.CONSUMABLE && curTab === TabType.SUB ? (
          <div className={styles.desc}>
            <span>{$t((v) => v.Text_UnlimitedCutout)}</span>
          </div>
        ) : (
          ''
        )}

        {/* 续期商品提示文案 */}
        {renewExplain ? (
          <div className={styles.desc}>
            <span>{renewExplain}</span>
            {renderQuestionIcon('renewedSubscription')}
          </div>
        ) : null}

        {/* 个人版，美豆商品 */}
        {!isTeamSubscription && curTab === TabType.MEIDOU ? (
          <div className={styles.descInfo}>
            <IconFont name="infoCircleBold" fontSize={18} color="rgba(255, 255, 255, 0.50)" />
            {curProduct.productId === '7427746831138048106'
              ? $t((v) => v.Text_ValidityThreeMonths)
              : $t((v) => v.Text_ValidityOneYear)}
          </div>
        ) : null}
      </>
    );
  };

  const handleRenderAddOnProducts = () => {
    if (!addOnProduct) return null;

    const thumbUrl = addOnProduct.rightsIcon?.materialUrl || '';

    return (
      <div className={styles.addOnCard} onClick={handleToggleAddOnSelected}>
        <div className={styles.addOnMain}>
          {thumbUrl ? (
            <div className={styles.addOnThumb}>
              <img className={styles.addOnThumbImg} src={thumbUrl} alt="" />
            </div>
          ) : null}
          <div className={styles.addOnInfo}>
            <span className={styles.addOnTitle}>{addOnProduct.showName}</span>
            <span className={styles.addOnHint}>{addOnProduct.commonDesc}</span>
          </div>
        </div>
        <div className={styles.addOnMeta}>
          <div className={styles.addOnPrices}>
            <span className={styles.addOnPrice}>
              {addOnProduct.addPrice?.moneySymbol}
              {(addOnProduct.addPrice?.price || 0) / 100}
            </span>
            {addOnProduct.addPrice?.originalPrice ? (
              <span className={styles.addOnOriginalPrice}>
                {addOnProduct.addPrice?.moneySymbol}
                {(addOnProduct.addPrice?.originalPrice || 0) / 100}
              </span>
            ) : null}
          </div>
          <span
            className={classnames(
              styles.addOnRadio,
              addOnProduct.selected && styles.addOnRadioSelected
            )}
          />
        </div>
      </div>
    );
  };

  return (
    <div
      className={styles.module}
      style={{
        minHeight: isTeamSubscription ? '20px' : undefined,
      }}
    >
      {handleRenderTips()}

      {handleRenderAddOnProducts()}

      <ServiceNoticeModal
        visible={visible}
        onClose={() => setVisible(false)}
        variant={type === 'consumable' ? 'consumable' : 'renewal'}
      />
    </div>
  );
};

export default React.memo(ProductTip);
